import {
  faCopy,
  faGear,
  faLayerGroup,
  faQuestion,
  faRectangleXmark,
  faShareFromSquare,
  faTrashCan,
  faPen,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Col, Dropdown, DropdownButton, Row } from "react-bootstrap";
import TagChipEditor from "../../CommonComponents/Tags/TagChipEditor";
import { resolveTags } from "../../../utils/resolveTags";
import CustomTooltip from "../../Elements/Tooltip";
import styles from "./linkCard.module.css";
import { networkService } from "../../config/axios";
import UserInfoContext from "../../../utils/userInfoContext";
import CommonModal from "../../Modals/CommonModal";
import Button from "../../Elements/Button";

const LinkCard = ({
  data,
  trigerCopyLaserLinkEvent,
  triggerSettingsLaserLinkEvent,
  triggerDeleteLaserLinkEvent,
  updateLinkData,
  triggerUdateRecord,
}) => {
  const [getCopyTextStatus, setCopyTextStatus] = useState(false);
  const [embedCode, setEmbedCode] = useState(null);
  const [embedCopied, setEmbedCopied] = useState(false);

  const fallbackCopy = (text) => {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  };

  const generateEmbedCode = () => {
    const link = data?.tracking_link;
    // KDF title (not the LaserLink name), escaped so quotes can't break the attribute
    const title = (data?.doc?.title ?? "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const geometry = data?.doc_geometry ?? null;
    const transparentBg = !!data?.enable_transparent_bg;
    const shadowBox = !!data?.enable_shadow_box;
    const w = geometry?.w || 16;
    const h = geometry?.h || 9;
    const extraStyles = [
      transparentBg ? "background:transparent;" : "",
      shadowBox ? "box-shadow:0 4px 24px rgba(0,0,0,0.25);border-radius:4px;" : "",
    ].join("");
    const iframeStyle = transparentBg
      ? "position:absolute;top:0;left:0;width:100%;height:100%;border:0;background:transparent;"
      : "position:absolute;top:0;left:0;width:100%;height:100%;border:0;";
    const allowTransparency = transparentBg ? ' allowtransparency="true"' : '';
    return `<div style="width:min(100%,calc(100vh * ${w} / ${h}));aspect-ratio:${w}/${h};position:relative;margin:0 auto;overflow:hidden;${extraStyles}"><iframe src="${link}" frameborder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write" allowfullscreen${allowTransparency} style="${iframeStyle}" title="${title}"></iframe></div>`;
  };
  const [editableTitle, setEditableTitle] = useState({
    status: false,
    value: data?.name,
    id: data?.id,
    docId: data?.doc?.id,
  });
  // Tracks the last successfully-saved title locally so the card can reflect
  // a save immediately without re-fetching the whole list (see handleOutsideOrEnter).
  const [savedTitle, setSavedTitle] = useState(data?.name);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const editableRef = useRef(null);
  const { setShowNotifications, userInfoData } = useContext(UserInfoContext);

  useEffect(() => {
    setSavedTitle(data?.name);
  }, [data?.name]);

  const [currentTags, setCurrentTags] = useState(data?.tags || []);

  useEffect(() => {
    setCurrentTags(data?.tags || []);
  }, [data?.tags]);

  const handleTagsChange = useCallback(
    async (nextTags) => {
      const resolvedTags = await resolveTags(nextTags, currentTags);
      setCurrentTags(resolvedTags);
      await updateLinkData(data, resolvedTags.map((tag) => tag.id), { skipRefresh: true });
    },
    [data, currentTags, updateLinkData]
  );

  const handleCopyTextEvent = useCallback(async (value) => {
    if (!value) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        // fallback for older browsers
        const textarea = document.createElement("textarea");
        textarea.value = value;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      setCopyTextStatus(true);
      setTimeout(() => setCopyTextStatus(false), 3000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  }, []);

  const handleUpdateEditableTitleEvent = useCallback((e) => {
    setEditableTitle((prev) => ({ ...prev, value: e.target.value }));
  }, []);

  useEffect(() => {
    setCopyTextStatus(false);
  }, []);

  const handleOutsideOrEnter = useCallback(async () => {
    if (isSavingTitle) return;
    try {
      if (editableRef.current != null) {
        const newTitle = editableRef.current.value;
        setIsSavingTitle(true);
        const { status } = await networkService(
          `links/${editableTitle?.id}.json`,
          "PATCH",
          {
            doc_id: editableTitle?.docId,
            id: editableTitle?.id,
            name: newTitle,
          }
        );
        if (status == 200) {
          setSavedTitle(newTitle);
          setEditableTitle((prev) => ({ ...prev, status: false, value: newTitle }));
          setShowNotifications({
            showToast: true,
            toastType: "success",
            toastMessage: "Changes Saved",
          });
        }
      }
    } catch (error) {
      if (error.response?.status == 422) {
        setEditableTitle((prev) => ({
          ...prev,
          status: false,
          value: savedTitle,
        }));
        setShowNotifications({
          showToast: true,
          toastType: "danger",
          toastMessage:
            "Sorry, Laser Link title must be unique. This title already exists.",
        });
      }
    } finally {
      setIsSavingTitle(false);
    }
  }, [isSavingTitle, editableTitle?.id, editableTitle?.docId, savedTitle, setShowNotifications]);

  const handleStartEditTitle = () => {
    setEditableTitle((prev) => ({ ...prev, status: true, value: savedTitle }));
  };

  const handleCancelTitleEdit = useCallback(() => {
    setEditableTitle((prev) => ({ ...prev, status: false, value: savedTitle }));
  }, [savedTitle]);

  useEffect(() => {
    if (!editableTitle.status) return;

    const handleClickOutside = (event) => {
      if (editableRef.current && !editableRef.current.contains(event.target)) {
        handleOutsideOrEnter();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        handleOutsideOrEnter();
      } else if (event.key === "Escape") {
        handleCancelTitleEdit();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [editableTitle.status, handleOutsideOrEnter, handleCancelTitleEdit]);

  return (
    <div className={styles.linkWrapper}>
      <div className={styles.linkHeader}>
        <Row className="align-items-center">
          <Col md={8}>
            <div className={styles.docTitle}>
              {data?.template && (
                <CustomTooltip
                  title={<p className="p-1 text-light">Template LaserLINK</p>}
                >
                  <FontAwesomeIcon icon={faLayerGroup} />
                </CustomTooltip>
              )}
              {editableTitle.status ? (
                <span className={styles.titleEditGroup}>
                  <input
                    type="text"
                    className={styles.titleInput}
                    value={editableTitle?.value}
                    ref={editableRef}
                    onChange={(e) => handleUpdateEditableTitleEvent(e)}
                    maxLength={40}
                    disabled={isSavingTitle}
                    autoFocus
                  />
                  <button
                    type="button"
                    className={styles.titleActionBtn}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleOutsideOrEnter}
                    disabled={isSavingTitle}
                    aria-label="Save title"
                  >
                    <FontAwesomeIcon icon={faCheck} />
                  </button>
                  <button
                    type="button"
                    className={`${styles.titleActionBtn} ${styles.titleActionBtnCancel}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleCancelTitleEdit}
                    disabled={isSavingTitle}
                    aria-label="Cancel"
                  >
                    <FontAwesomeIcon icon={faRectangleXmark} />
                  </button>
                </span>
              ) : (
                <span className={styles.titleDisplayGroup}>
                  <strong onClick={handleStartEditTitle}>{savedTitle}</strong>
                  <button
                    type="button"
                    className={styles.titleEditPencil}
                    onClick={handleStartEditTitle}
                    aria-label="Edit title"
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                </span>
              )}
            </div>
          </Col>
          <Col
            md={4}
            className={`d-flex justify-content-between align-items-center ${styles.actionWrapper}`}
          >
            <CustomTooltip
              title={<p className="p-1 text-light">Clone this LaserLINK</p>}
            >
              {/* <FontAwesomeIcon icon={faCopy} onClick={() => trigerCopyLaserLinkEvent(data.id)} className="cursor-pointer fs-6" /> */}
              <img
                src="/icons/copy.svg"
                alt="Share"
                className="cursor-pointer"
                onClick={() => trigerCopyLaserLinkEvent(data.id)}
              />
            </CustomTooltip>
            <CustomTooltip title={<p className="p-1 text-light">Settings</p>}>
              <img
                src="/icons/settings.svg"
                className="cursor-pointer"
                alt="Share"
                onClick={() => triggerSettingsLaserLinkEvent(data)}
              />
            </CustomTooltip>
            <CustomTooltip
              title={<p className="p-1 text-light">Remove LaserLINK</p>}
            >
              {/* <FontAwesomeIcon icon={faTrashCan} className="cursor-pointer fs-6" onClick={() => triggerDeleteLaserLinkEvent(data.id)} /> */}
              <img
                src="/icons/delete.svg"
                className="cursor-pointer"
                alt="Share"
                onClick={() => triggerDeleteLaserLinkEvent(data.id)}
              />
            </CustomTooltip>

            <div className={styles.share}>
              {!data?.template && data?.language_flag?.class && (
                <CustomTooltip
                  title={
                    <p className="p-1 text-light">
                      {data?.language_flag?.name ?? "Language"}
                    </p>
                  }
                >
                  <div
                    className={`${styles.languageFlagMini} ${data?.language_flag?.class}`}
                  ></div>
                </CustomTooltip>
              )}

              {!data?.template && data?.set_as_embed ? (
                <CustomTooltip title={<p className="p-1 text-light">Copy embed code</p>}>
                  <button
                    className="btn"
                    style={{ padding: "6px 12px", backgroundColor: "#294561", color: "#fff", border: "none" }}
                    onClick={() => setEmbedCode(generateEmbedCode())}
                  >
                    <span style={{ fontFamily: "monospace", marginRight: 3, fontSize: "11px" }}>&lt;/&gt;</span>
                    Embed
                  </button>
                </CustomTooltip>
              ) : !data?.template && (
                <DropdownButton
                  id={`dropdown-button-drop-${"up"}`}
                  drop={"up"}
                  variant="none"
                  title={
                    <>
                      {" "}
                      <img src="/icons/ios_share.svg" alt="Share" />
                     <span>&nbsp;Share&nbsp;{" "}</span>
                    </>
                  }
                  className="shareButtonIcon"
                >
                  <Dropdown.Item
                    href={`https://www.facebook.com/sharer/sharer.php?u=${data?.tracking_link}`}
                    target="_blank"
                  >
                    {/* <FontAwesomeIcon icon={faFacebookF} />  */}
                    Facebook
                  </Dropdown.Item>
                  <Dropdown.Item
                    href={`https://twitter.com/intent/tweet?text=${data?.tracking_link}`}
                    target="_blank"
                  >
                    {/* <FontAwesomeIcon icon={faTwitter} />  */}
                    Twitter
                  </Dropdown.Item>
                  <Dropdown.Item
                    href={`mailto:?subject=Check%20this%20out&body=${data?.tracking_link}`}
                    target="_blank"
                  >
                    {/* <FontAwesomeIcon icon={faEnvelope} />  */}
                    Email
                  </Dropdown.Item>
                  <Dropdown.Item
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=https://powered-by.kinetizine.com/MTczMTkwODAwOA/en-US&source=LinkedIn`}
                    target="_blank"
                  >
                    {/* <FontAwesomeIcon icon={faLinkedin} /> */}
                    LinkedIn
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() =>
                      handleCopyTextEvent(data?.tracking_link ?? "")
                    }
                  >
                    <strong style={{ marginLeft: 0 }}>{getCopyTextStatus ? "Link Copied!" : "Copy Link"}</strong>
                  </Dropdown.Item>
                </DropdownButton>
              )}
            </div>
          </Col>
        </Row>
      </div>
      <CommonModal
        modalStatus={embedCode !== null}
        title={"Embed"}
        footer={
          <Button
            text={embedCopied ? "Copied! ✓" : "Copy embed code </>"}
            classList={"success laser-link-btn"}
            addonClass={"my-2 mx-1"}
            onClick={() => {
              const text = embedCode ?? "";
              if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
              } else {
                fallbackCopy(text);
              }
              setEmbedCopied(true);
              setTimeout(() => {
                setEmbedCopied(false);
                setEmbedCode(null);
              }, 1500);
            }}
          />
        }
        changeActiveStatus={(status) => { if (!status) { setEmbedCode(null); setEmbedCopied(false); } }}
        classList={"embed-modal"}
        centered
      >
        <textarea
          value={embedCode ?? ""}
          onChange={(em) => setEmbedCode(em.target.value)}
          style={{ width: "100%" }}
          rows={10}
        />
      </CommonModal>
      <div id="link-list-top" className={styles.linkBody}>
        <Row className="align-items-center">
          <Col md={(data?.thumb || data?.thumbnail) ? 9 : 12} className={styles.linkBodyCol}>
            <div className={`${styles.docContent}`}>
              <CustomTooltip
                title={
                  <p className="p-1 text-light">
                    Display all LaserLINX for this KDF
                  </p>
                }
              >
                <span className="text-link-success">KDF: </span>
              </CustomTooltip>
              <CustomTooltip
                title={<p className="p-1 text-light">KDF Settings</p>}
              >
                <a
                  href={`/kdfs/${data?.doc?.id}`}
                  className="text-link-success"
                >
                  {" "}
                  {data?.doc?.title}
                </a>
              </CustomTooltip>
            </div>
            <div className={`${styles.docContent} my-2`}>
              <div className="d-flex">
                <span>ET:&nbsp;</span>
                <CustomTooltip
                  title={<p className="p-1 text-light">Engagement Threshold</p>}
                >
                  <span>{data?.page_viewed_sec ?? 0} secs</span>
                </CustomTooltip>
                {data?.domain && (
                  <CustomTooltip
                    title={
                      <p className="p-1 text-light">
                        Your chosen domain aims to enhance recipient trust and
                        engagement.
                      </p>
                    }
                  >
                    <p className="color-blue mx-1">{data?.domain ?? " "}</p>
                  </CustomTooltip>
                )}
                {data?.has_optin && (
                  <CustomTooltip
                    title={
                      <p className="p-1 text-light">
                        {data?.tooltip_expire_time}
                      </p>
                    }
                  >
                    <span>Access Gate</span>
                  </CustomTooltip>
                )}
              </div>
              <CustomTooltip
                title={
                  <p className="p-1 text-light">
                    Your chosen domain aims to ehance recipient trust and
                    engagement.
                  </p>
                }
              >
                <strong className="text-link-primary">
                  {" "}
                  {data?.doc?.domain}
                </strong>
              </CustomTooltip>
            </div>
            <div className={`${styles.docContent} my-2`}>
              <span className="">Created by: </span>
              <CustomTooltip
                title={
                  <p className="p-1 text-light">{data?.date_created ?? "-"}</p>
                }
              >
                <strong> {data?.username}</strong>
              </CustomTooltip>
              {!data?.creator_has_geo_provider && (
                <CustomTooltip
                  title={
                    <p className="p-1 text-light">
                      {String(data?.user_id) === String(userInfoData?.id)
                        ? "You haven't set up a geolocation provider yet — recipient location can't be tracked for this link until you add one in My Profile."
                        : "This link's creator hasn't set up a geolocation provider — recipient location can't be tracked for this link."}
                    </p>
                  }
                >
                  <span className="ms-2 text-warning">⚠</span>
                </CustomTooltip>
              )}
            </div>
            {data?.receivers?.length > 0 && (
              <div className={`${styles.docContent} my-2`}>
                <span className="">Recipient: </span>
                <CustomTooltip
                  title={
                    <p className="p-1 text-light">
                      {data?.receivers[0]?.email ?? "-"}
                    </p>
                  }
                >
                  <a href={`/tl-stats/${data.token}#/views`}>
                    <strong>
                      {" "}
                      {data?.receivers[0]?.name}{" "}
                      {data?.receivers?.length > 1 &&
                        `(and ${data?.receivers?.length - 1} more)`}{" "}
                    </strong>
                  </a>
                </CustomTooltip>
              </div>
            )}
            <div className="custom-tag-wrapper kdf-settings">
              <span id="kdf-tags" className={styles.tagContent}>
                <span className={styles.tagLabel}>Tags:</span>
                <TagChipEditor
                  tags={currentTags}
                  onChange={handleTagsChange}
                  placeholderText="Add LaserLink tag..."
                  minWidthCh={16}
                  collapsible
                />
              </span>
            </div>
          </Col>
          {(data?.thumb || data?.thumbnail) && (
            <Col md={3} className="d-flex align-items-center justify-content-center">
              <a href={data?.viewer_preview} target="_blank" rel="noopener" className={styles.laserlinkImgList}>
                <img src={data?.thumb || data?.thumbnail} alt={data?.name} />
              </a>
            </Col>
          )}
        </Row>
      </div>
      {data?.stats?.viewers != 0 ? (
        <div className={styles.linkFooter}>
                <Row>
                  <div className={styles.stats}>
                    {!data?.template ? (
                      <>
                        <a
                          href={`/tl-stats/${data?.token}#/views`}
                          className={styles.statsButton}
                        >
                          <strong className="d-block">
                            {data?.stats?.viewers}
                          </strong>
                          <span>Visits</span>
                        </a>
                          <a
                            href={`/tl-stats/${data?.token}#/at-a-glance`}
                            className={styles.statsButton}
                          >
                            <CustomTooltip
                              title={
                                data?.stats?.duration_breakdown ? (
                                  data.stats.duration_breakdown.count >= 2 ? (
                                    <div className="p-1 text-light">
                                      <div>Median: {data.stats.duration_breakdown.median}</div>
                                      <div>Average: {data.stats.duration_breakdown.avg}</div>
                                      <div>Max: {data.stats.duration_breakdown.max}</div>
                                    </div>
                                  ) : (
                                    <div className="p-1 text-light">
                                      <div>Duration: {data.stats.duration_breakdown.max}</div>
                                    </div>
                                  )
                                ) : null
                              }
                            >
                              <strong className="d-block">
                                {data?.stats?.min_viewed || data?.link_total_sec_viewed?.replace(/^Total time:\s*/i, '')}
                              </strong>
                            </CustomTooltip>
                            <CustomTooltip placement="bottom" title={<p className="p-1 text-light">Total viewing time: {data?.stats?.min_viewed_secs}</p>}>
                              <span>Duration</span>
                            </CustomTooltip>
                          </a>
                        {data?.stats?.optins?.length > 0 && (
                          <CustomTooltip
                            title={
                              <div className="p-1 text-light">
                                {parseInt(data?.stats?.optin_breakdown?.optins, 10) > 0 && (
                                  <div>{data.stats.optin_breakdown.optins} Optin</div>
                                )}
                                {parseInt(data?.stats?.optin_breakdown?.multilink_optins, 10) > 0 && (
                                  <div>{data.stats.optin_breakdown.multilink_optins} MultiLink Campaign</div>
                                )}
                                <div>{data?.opt_in_reject} Bounced</div>
                              </div>
                            }
                          >
                            <a
                              href={`/tl-stats/${data?.token}#/views`}
                              className={styles.statsButton}
                            >
                              <strong className="d-block">
                                {data?.stats?.optins?.length}
                              </strong>
                              <span>
                                Opt-in{data?.stats?.optins?.length > 1 && "s"}
                              </span>
                            </a>
                          </CustomTooltip>
                        )}
                        <CustomTooltip
                          title={
                            <p className="p-1 text-light">
                              {data?.link_total_sec_viewed}
                            </p>
                          }
                        >
                          <a
                            href={`/tl-stats/${data?.token}#/at-a-glance`}
                            className={styles.statsButton}
                          >
                            <strong className="d-block">
                              {data?.stats?.percent_viewed}
                            </strong>
                            <span>Viewed</span>
                          </a>
                        </CustomTooltip>
                        {data?.last_viewed && (
                          <a
                            href={`/tl-stats/${data.token}#/at-a-glance`}
                            className={styles.statsButton}
                          >
                            <strong className="d-block">
                              {data?.last_viewed}
                            </strong>
                            <span>Last Viewed</span>
                          </a>
                        )}
                      </>
                    ) : (
                      ""
                    )}
                  </div>
                </Row>
        </div>
      ) : null}

    </div>
    // </Container>
  );
};
export default React.memo(LinkCard);
